if (!customElements.get('custom-select')) {
  class CustomSelect extends HTMLElement {
    constructor() {
      super();
      this.button = this.querySelector('.custom-select__btn');
      this.listbox = this.querySelector('.custom-select__listbox');
      this.options = this.querySelectorAll('.custom-select__option');
      this.selectedOption = this.querySelector('[aria-selected="true"]');
      this.nativeSelect = document.getElementById(`${this.id}-native`);
      this.swatches = !!Array.from(this.options).find((el) => 'swatch' in el.dataset);
      // Rich rows (thumbnail + value + stock + price) are opt-in from
      // variant-picker.liquid for an option row with more values than the
      // "Values before switching to a dropdown" theme setting. Every other host
      // of this component renders plain text rows and takes none of the
      // branches below.
      this.richRows = this.button.classList.contains('custom-select__btn--rich');
      this.focusedClass = 'is-focused';
      this.searchString = '';
      this.listboxOpen = false;

      // Set the selected option.
      if (!this.selectedOption) {
        this.selectedOption = this.listbox.firstElementChild;
      }

      this.addListeners();
      this.setButtonWidth();
    }

    addListeners() {
      this.addEventListener('keydown', this.handleKeydown.bind(this));
      this.button.addEventListener('mousedown', this.handleMousedown.bind(this));
    }

    /**
     * Returns the next visible sibling element
     * @param {Element} elem - The element to search
     * @returns {Element}
     */
    static getNextVisibleSibling(elem) {
      let sibling = elem.nextElementSibling;
      while (sibling) {
        if (sibling.offsetParent) return sibling;
        sibling = sibling.nextElementSibling;
      }
      return null;
    }

    /**
     * Returns the previous visible sibling element
     * @param {Element} elem - The element to search
     * @returns {Element}
     */
    static getPreviousVisibleSibling(elem) {
      let sibling = elem.previousElementSibling;
      while (sibling) {
        if (sibling.offsetParent) return sibling;
        sibling = sibling.previousElementSibling;
      }
      return null;
    }

    /**
     * Adds event listeners when the options list is open.
     */
    addListboxOpenListeners() {
      this.mouseoverHandler = this.handleMouseover.bind(this);
      this.mouseleaveHandler = this.handleMouseleave.bind(this);
      this.clickHandler = this.handleClick.bind(this);
      this.blurHandler = this.handleBlur.bind(this);

      this.listbox.addEventListener('mouseover', this.mouseoverHandler);
      this.listbox.addEventListener('mouseleave', this.mouseleaveHandler);
      this.listbox.addEventListener('click', this.clickHandler);
      this.listbox.addEventListener('blur', this.blurHandler);
    }

    /**
     * Removes event listeners added when the options list was open.
     */
    removeListboxOpenListeners() {
      this.listbox.removeEventListener('mouseover', this.mouseoverHandler);
      this.listbox.removeEventListener('mouseleave', this.mouseleaveHandler);
      this.listbox.removeEventListener('click', this.clickHandler);
      this.listbox.removeEventListener('blur', this.blurHandler);
    }

    /**
     * Handles 'keydown' events on the custom select element.
     * @param {object} evt - Event object.
     */
    handleKeydown(evt) {
      if (this.listboxOpen) {
        this.handleKeyboardNav(evt);
      } else if (evt.key === 'ArrowUp' || evt.key === 'ArrowDown' || evt.key === ' ') {
        evt.preventDefault();
        this.showListbox();
      }
    }

    /**
     * Handles 'mousedown' events on the button element.
     * @param {object} evt - Event object.
     */
    handleMousedown(evt) {
      if (!this.listboxOpen && evt.button === 0) {
        this.showListbox();
      }
    }

    /**
     * Handles 'mouseover' events on the options list.
     * @param {object} evt - Event object.
     */
    handleMouseover(evt) {
      // closest(), not matches('li'): a rich row nests a thumbnail and several
      // spans, so the event target is often a descendant and the highlight
      // would die as soon as the pointer crossed the image.
      const option = evt.target.closest('li.custom-select__option');
      if (option) {
        // A rich list never scrolls on a pointer move — see focusOption().
        this.focusOption(option, false, !this.richRows);
      }
    }

    /**
     * Handles 'mouseleave' events on the options list.
     */
    handleMouseleave() {
      const optionToFocus = this.selectedOption.hasAttribute('hidden')
        ? this.listbox.querySelector('li:not([hidden])')
        : this.selectedOption;
      if (optionToFocus) {
        // Re-highlights the selection without scrolling to it on a rich list:
        // the base behaviour snapped a list scrolled to its end back to the
        // selected row the moment the pointer left the panel — including after
        // dragging the scrollbar and releasing just past its edge.
        this.focusOption(optionToFocus, false, !this.richRows);
      }
    }

    /**
     * Handles 'click' events on the custom select element.
     * @param {object} evt - Event object.
     */
    handleClick(evt) {
      // mouse down + move + up will trigger a click event on parent - only respond to clicks on LI.
      // closest(), not tagName: on a rich row a click that lands on the
      // thumbnail targets the <img>, and selection would silently do nothing.
      const option = evt.target.closest('li.custom-select__option');
      if (option) {
        this.selectOption(option);
      }
    }

    /**
     * Handles 'blur' events on the options list.
     */
    handleBlur() {
      if (this.listboxOpen) {
        this.hideListbox();
      }
    }

    /**
     * Handles 'keydown' events on the options list.
     * @param {object} evt - Event object.
     */
    handleKeyboardNav(evt) {
      let optionToFocus;

      // Disable tabbing if options list is open (as per native select element).
      if (evt.key === 'Tab') {
        evt.preventDefault();
      }

      switch (evt.key) {
        // Focus an option.
        case 'ArrowUp':
        case 'ArrowDown':
          evt.preventDefault();

          if (evt.key === 'ArrowUp') {
            optionToFocus = CustomSelect.getPreviousVisibleSibling(this.focusedOption);
          } else {
            optionToFocus = CustomSelect.getNextVisibleSibling(this.focusedOption);
          }

          if (optionToFocus && !optionToFocus.classList.contains('is-disabled')) {
            this.focusOption(optionToFocus, true);
          }
          break;

        // Select an option.
        case 'Enter':
        case ' ':
          evt.preventDefault();
          this.selectOption(this.focusedOption);
          break;

        // Cancel and close the options list.
        case 'Escape':
          evt.preventDefault();
          this.hideListbox();
          break;

        // Search for an option and focus the first match (if one exists).
        default:
          optionToFocus = this.findOption(evt.key);

          if (optionToFocus) {
            this.focusOption(optionToFocus, true);
          }
          break;
      }
    }

    /**
     * Sets the button width to the same as the longest option, to prevent
     * the button width from changing depending on the option selected.
     */
    setButtonWidth() {
      // A rich row is as wide as thumbnail + value + stock + price, and sizing
      // the closed button to that would stretch it across the whole column.
      // The button shows only the thumbnail and the value, so CSS owns its
      // width instead.
      if (this.richRows) return;

      // Get the width of an element without side padding.
      const getHorizontalPadding = (el) => {
        const elStyle = getComputedStyle(el);
        return parseFloat(elStyle.paddingLeft) + parseFloat(elStyle.paddingRight);
      };

      const buttonPadding = getHorizontalPadding(this.button);
      const optionPadding = getHorizontalPadding(this.selectedOption);
      const buttonBorder = this.button.offsetWidth - this.button.clientWidth;
      const optionWidth = Math.ceil(this.selectedOption.getBoundingClientRect().width);

      this.button.style.setProperty('--custom-select-button-width', `${optionWidth - optionPadding + buttonPadding + buttonBorder}px`);
    }

    /**
     * Shows the options list.
     */
    showListbox() {
      this.listbox.hidden = false;
      this.listboxOpen = true;

      this.classList.add('is-open');
      this.button.setAttribute('aria-expanded', 'true');
      this.listbox.setAttribute('aria-hidden', 'false');

      // Slight delay required to prevent blur event being fired immediately.
      setTimeout(() => {
        const optionToFocus = this.selectedOption.hasAttribute('hidden')
          ? this.listbox.querySelector('li:not([hidden])')
          : this.selectedOption;
        if (optionToFocus) {
          this.focusOption(optionToFocus, true);
        }
        this.listbox.focus();

        this.addListboxOpenListeners();
      });
    }

    /**
     * Hides the options list.
     */
    hideListbox() {
      if (!this.listboxOpen) return;

      this.listbox.hidden = true;
      this.listboxOpen = false;

      this.classList.remove('is-open');
      this.button.setAttribute('aria-expanded', 'false');
      this.listbox.setAttribute('aria-hidden', 'true');

      if (this.focusedOption) {
        this.focusedOption.classList.remove(this.focusedClass);
        this.focusedOption = null;
      }

      this.button.focus();
      this.removeListboxOpenListeners();
    }

    /**
     * Finds a matching option from a typed string.
     * @param {string} key - Key pressed.
     * @returns {?Element}
     */
    findOption(key) {
      this.searchString += key;

      // If there's a timer already running, clear it.
      if (this.searchTimer) {
        clearTimeout(this.searchTimer);
      }

      // Wait 500ms to see if another key is pressed, if not then clear the search string.
      this.searchTimer = setTimeout(() => {
        this.searchString = '';
      }, 500);

      // Find an option that contains the search string (if there is one).
      // Skip hidden options: a non-rendered element still reports its text via
      // innerText, so a hidden default option ("Please select") would otherwise be
      // matchable and typeahead would move the highlight onto something invisible.
      // On a rich row innerText also carries the stock wording and the price,
      // so typing "2" would jump to whichever row happens to cost 2.319 kr.
      // Match the value itself there.
      const matchingOption = Array.from(this.options).find((option) => {
        const label = this.richRows
          ? (option.dataset.value || '').toLowerCase()
          : option.innerText.toLowerCase();
        return label.includes(this.searchString)
          && !option.hidden
          && !option.classList.contains('is-disabled');
      });

      return matchingOption;
    }

    /**
     * Focuses an option.
     * @param {Element} option - <li> element of the option to focus.
     * @param {boolean} [clearOfHint=false] - On a rich list, keep the option
     *   above the scroll fade at the panel floor rather than flush on it.
     * @param {boolean} [scrollIntoView=true] - Scroll the list to bring the
     *   option into view. Off only for pointer paths on a rich list.
     */
    focusOption(option, clearOfHint = false, scrollIntoView = true) {
      // Remove focus on currently focused option (if there is one).
      if (this.focusedOption) {
        this.focusedOption.classList.remove(this.focusedClass);
      }

      // Set focus on the option.
      this.focusedOption = option;
      this.focusedOption.classList.add(this.focusedClass);

      // Pointer paths never scroll a rich list, because a shopper who scrolls
      // must find the list where they left it (ClickUp 869f9n6zj, 2 Oct): the
      // pointer leaving the panel snapped it back to the selected row, and a
      // part-cut-off row sliding under a resting pointer fires mouseover and
      // dragged the list back to show it. The pointer moves the highlight
      // only, as on a native select. Keyboard, typeahead and open still scroll,
      // so the next arrow key after a hover brings that row into view.
      if (!scrollIntoView) return;

      // If option is out of view, scroll the list.
      if (this.listbox.scrollHeight > this.listbox.clientHeight) {
        // A rich list paints a fade over its bottom 4rem (product.css), so a row
        // scrolled flush onto the floor would sit under it — on every open, that
        // is the shopper's own selection (ClickUp 869f9n6zj). The inset is read
        // from the listbox's scroll-padding-bottom, where the CSS declares it.
        // Open, the arrow keys and typeahead ask for it.
        const inset = clearOfHint && this.richRows
          ? parseFloat(getComputedStyle(this.listbox).scrollPaddingBottom) || 0
          : 0;
        const scrollBottom = this.listbox.clientHeight + this.listbox.scrollTop - inset;
        const optionBottom = option.offsetTop + option.offsetHeight;

        if (optionBottom > scrollBottom) {
          this.listbox.scrollTop = optionBottom - this.listbox.clientHeight + inset;
        } else if (option.offsetTop < this.listbox.scrollTop) {
          this.listbox.scrollTop = option.offsetTop;
        }
      }
    }

    /**
     * Copies a rich option's thumbnail and value into the closed button.
     *
     * The button shows only those two on purpose — the stock line and the price
     * belong to the open list, and copying the whole row in (which is what the
     * plain-row `firstElementChild.textContent` line does) would put
     * "Sort På lager 2.319 kr" on the button.
     * @param {Element} option - Option <li> element.
     */
    updateRichButton(option) {
      const buttonText = this.button.querySelector('.custom-select__btn-text');
      const buttonImage = this.button.querySelector('.custom-select__btn-image');
      const optionTitle = option.querySelector('.custom-select__option-title');
      const optionImage = option.querySelector('.custom-select__option-img');

      if (buttonText) {
        // The "Please select" row carries no title element of its own.
        buttonText.textContent = (optionTitle || option).textContent.trim();
      }

      // The wrapper is always rendered, even when nothing is selected yet, so
      // there is an element to fill here rather than one to build.
      if (!buttonImage) return;
      if (optionImage) {
        const img = optionImage.cloneNode(true);
        img.className = 'custom-select__btn-img';
        buttonImage.replaceChildren(img);
      } else {
        buttonImage.replaceChildren();
      }
    }

    /**
     * Selects an option.
     * @param {Element} option - Option <li> element.
     */
    selectOption(option) {
      if (option !== this.selectedOption) {
        // Switch aria-selected attribute to selected option.
        option.setAttribute('aria-selected', 'true');
        this.selectedOption.setAttribute('aria-selected', 'false');

        // Update swatch colour in the button.
        if (this.swatches) {
          this.button.dataset.swatch = option.dataset.swatch || '';
          const nativeSwatchColor = option.style.getPropertyValue('--native-swatch-color');
          if (nativeSwatchColor) {
            this.button.style.setProperty('--native-swatch-color', nativeSwatchColor);
            this.button.style.setProperty('--native-swatch-image', '');
          }
          const nativeSwatchImage = option.style.getPropertyValue('--native-swatch-image');
          if (nativeSwatchImage) {
            this.button.style.setProperty('--native-swatch-image', nativeSwatchImage);
            this.button.style.setProperty('--native-swatch-color', '');
          }
        }

        // Update the button text and set the option as active.
        if (this.richRows) {
          this.updateRichButton(option);
        } else {
          this.button.firstElementChild.textContent = option.firstElementChild.textContent;
        }
        this.listbox.setAttribute('aria-activedescendant', option.id);
        this.selectedOption = document.getElementById(option.id);

        // If a native <select> exists, update its selected value and dispatch a 'change' event.
        if (this.nativeSelect) {
          this.nativeSelect.value = option.dataset.value;
          this.nativeSelect.dispatchEvent(new Event('change', { bubbles: true }));
        } else {
          // Dispatch a 'change' event on the custom select element.
          const detail = {
            selectedValue: option.dataset.value,
            productUrl: option.dataset.productUrl,
            variantId: option.dataset.variantId
          };
          this.dispatchEvent(new CustomEvent('change', { bubbles: true, detail }));
        }
      }

      this.hideListbox();
    }
  }

  customElements.define('custom-select', CustomSelect);
}
